import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Tag, FolderOpen, GripVertical, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';

const CategoryTagManagement = ({ onCategoriesChange, onTagsChange }) => {
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [showEditTag, setShowEditTag] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  
  const [newCategory, setNewCategory] = useState({ name: '', color: '#FF4500', sort_order: 0 });
  const [newTag, setNewTag] = useState({ name: '', color: '#FF4500' });

  useEffect(() => {
    fetchCategories();
    fetchTags();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
      if (onCategoriesChange) onCategoriesChange(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get('/tags');
      setTags(response.data);
      if (onTagsChange) onTagsChange(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      await api.post('/categories', {
        ...newCategory,
        sort_order: categories.length
      });
      toast.success('Category added');
      setShowAddCategory(false);
      setNewCategory({ name: '', color: '#FF4500', sort_order: 0 });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory?.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      await api.put(`/categories/${editingCategory.id}`, editingCategory);
      toast.success('Category updated');
      setShowEditCategory(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!confirm(`Delete category "${category.name}"? Products in this category will need to be reassigned.`)) {
      return;
    }
    try {
      await api.delete(`/categories/${category.id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const handleAddTag = async () => {
    if (!newTag.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    try {
      await api.post('/tags', newTag);
      toast.success('Tag added');
      setShowAddTag(false);
      setNewTag({ name: '', color: '#FF4500' });
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tag');
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag?.name.trim()) {
      toast.error('Tag name is required');
      return;
    }
    try {
      await api.put(`/tags/${editingTag.id}`, editingTag);
      toast.success('Tag updated');
      setShowEditTag(false);
      setEditingTag(null);
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tag');
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!confirm(`Delete tag "${tag.name}"? It will be removed from all products.`)) {
      return;
    }
    try {
      await api.delete(`/tags/${tag.id}`);
      toast.success('Tag deleted');
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tag');
    }
  };

  const colorPresets = [
    '#FF4500', '#FF5722', '#E91E63', '#9C27B0', '#673AB7',
    '#3F51B5', '#2196F3', '#00BCD4', '#009688', '#4CAF50',
    '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800'
  ];

  return (
    <div className="space-y-6">
      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="bg-[#1A1A1A] border border-white/10 mb-4">
          <TabsTrigger value="categories" className="text-sm">
            <FolderOpen className="w-4 h-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="tags" className="text-sm">
            <Tag className="w-4 h-4 mr-2" />
            Tags
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">Product Categories</h3>
              <p className="text-sm text-gray-400">Organize your menu items</p>
            </div>
            <Button
              onClick={() => setShowAddCategory(true)}
              className="bg-[#FF4500] hover:bg-[#FF5722] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>

          <div className="space-y-2">
            {categories.map((category, index) => (
              <Card key={category.id} className="bg-[#1A1A1A] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GripVertical className="w-5 h-5 text-gray-500 cursor-grab" />
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color || '#FF4500' }}
                      />
                      <span className="text-white font-medium">{category.name}</span>
                      <span className="text-xs text-gray-500">#{index + 1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingCategory(category);
                          setShowEditCategory(true);
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteCategory(category)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">Product Tags</h3>
              <p className="text-sm text-gray-400">Add labels like "Spicy", "Bestseller", etc.</p>
            </div>
            <Button
              onClick={() => setShowAddTag(true)}
              className="bg-[#00C853] hover:bg-[#00E676] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Tag
            </Button>
          </div>

          <div className="flex flex-wrap gap-3">
            {tags.map((tag) => (
              <div
                key={tag.id}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#1A1A1A] border border-white/10 group"
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="text-white text-sm">{tag.name}</span>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => {
                      setEditingTag(tag);
                      setShowEditTag(true);
                    }}
                    className="p-1 hover:bg-white/10 rounded"
                  >
                    <Edit className="w-3 h-3 text-gray-400" />
                  </button>
                  <button
                    onClick={() => handleDeleteTag(tag)}
                    className="p-1 hover:bg-red-500/20 rounded"
                  >
                    <X className="w-3 h-3 text-gray-400 hover:text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="bg-[#121212] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Category Name *</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Desserts"
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white">Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewCategory({ ...newCategory, color })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      newCategory.color === color ? 'border-white scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <Button onClick={handleAddCategory} className="w-full bg-[#FF4500] hover:bg-[#FF5722]">
              Add Category
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditCategory} onOpenChange={setShowEditCategory}>
        <DialogContent className="bg-[#121212] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Category</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <div className="space-y-4">
              <div>
                <Label className="text-white">Category Name *</Label>
                <Input
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Color</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {colorPresets.map((color) => (
                    <button
                      key={color}
                      onClick={() => setEditingCategory({ ...editingCategory, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        editingCategory.color === color ? 'border-white scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <Button onClick={handleUpdateCategory} className="w-full bg-[#00C853] hover:bg-[#00E676]">
                Update Category
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Tag Dialog */}
      <Dialog open={showAddTag} onOpenChange={setShowAddTag}>
        <DialogContent className="bg-[#121212] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white">Tag Name *</Label>
              <Input
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                placeholder="e.g., Gluten-Free"
                className="bg-white/5 border-white/10 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white">Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewTag({ ...newTag, color })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      newTag.color === color ? 'border-white scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            <Button onClick={handleAddTag} className="w-full bg-[#00C853] hover:bg-[#00E676]">
              Add Tag
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tag Dialog */}
      <Dialog open={showEditTag} onOpenChange={setShowEditTag}>
        <DialogContent className="bg-[#121212] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Tag</DialogTitle>
          </DialogHeader>
          {editingTag && (
            <div className="space-y-4">
              <div>
                <Label className="text-white">Tag Name *</Label>
                <Input
                  value={editingTag.name}
                  onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Color</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {colorPresets.map((color) => (
                    <button
                      key={color}
                      onClick={() => setEditingTag({ ...editingTag, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${
                        editingTag.color === color ? 'border-white scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <Button onClick={handleUpdateTag} className="w-full bg-[#00C853] hover:bg-[#00E676]">
                Update Tag
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryTagManagement;
